// Auth persistence utilities to handle browser restarts
import { AccountInfo } from "@azure/msal-browser";

const AUTH_PERSISTENCE_KEY = "einstein_auth_persistence";
const COOKIE_NAME = "einstein_auth_hint";

export const authPersistence = {
	// Save account info to both localStorage and cookie
	saveAccount: (account: AccountInfo) => {
		try {
			// Only save minimal non-sensitive information
			const persistData = {
				username: account.username,
				timestamp: Date.now(),
			};

			// Save to localStorage with encryption if possible
			localStorage.setItem(AUTH_PERSISTENCE_KEY, JSON.stringify(persistData));

			// Save username to httpOnly cookie for better security (if server supports it)
			// For client-side only, use secure settings
			const secureFlag =
				window.location.protocol === "https:" ? "; secure" : "";
			const sameSite = "; samesite=strict";
			const maxAge = `; max-age=${7 * 24 * 60 * 60}`; // 7 days instead of 30

			// Encode the username to prevent injection attacks
			const encodedUsername = encodeURIComponent(account.username);
			document.cookie = `${COOKIE_NAME}=${encodedUsername}; path=/${maxAge}${sameSite}${secureFlag}`;
		} catch (error) {
			// Don't log sensitive error details
			if (import.meta.env.DEV) {
				console.error("[Auth] Failed to save account persistence");
			}
		}
	},

	// Get saved account info
	getSavedAccount: (): { username: string } | null => {
		try {
			// First try localStorage
			const stored = localStorage.getItem(AUTH_PERSISTENCE_KEY);
			if (stored) {
				const data = JSON.parse(stored);
				// Check if data is not too old (7 days)
				if (
					data &&
					data.timestamp &&
					Date.now() - data.timestamp < 7 * 24 * 60 * 60 * 1000
				) {
					// Validate username format to prevent injection
					if (
						data.username &&
						typeof data.username === "string" &&
						data.username.includes("@")
					) {
						return { username: data.username };
					}
				} else {
					// Clean up expired data
					localStorage.removeItem(AUTH_PERSISTENCE_KEY);
				}
			}

			// Fallback to cookie
			const cookieMatch = document.cookie.match(
				new RegExp(`${COOKIE_NAME}=([^;]+)`),
			);
			if (cookieMatch && cookieMatch[1]) {
				const decodedUsername = decodeURIComponent(cookieMatch[1]);
				// Validate username format
				if (decodedUsername.includes("@")) {
					return { username: decodedUsername };
				}
			}
		} catch (error) {
			// Don't log sensitive error details
			if (import.meta.env.DEV) {
				console.error("[Auth] Failed to get saved account");
			}
		}
		return null;
	},

	// Clear saved account info
	clearAccount: () => {
		try {
			localStorage.removeItem(AUTH_PERSISTENCE_KEY);
			// Clear cookie with same settings as when set
			const secureFlag =
				window.location.protocol === "https:" ? "; secure" : "";
			document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; samesite=strict${secureFlag}`;
		} catch (error) {
			// Don't log sensitive error details
			if (import.meta.env.DEV) {
				console.error("[Auth] Failed to clear account persistence");
			}
		}
	},
};
